/**
 * Unified Credit Control Dashboard
 * Combines the best elements from Homepage and Dashboard V3
 * Updated: 2026-01-08
 * HMR stability verified
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { 
  RefreshCw, 
  Info, 
  TrendingDown, 
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  X,
  CheckCircle2,
  ListChecks,
  CreditCard,
  Target,
  Scale,
  ArrowRight,
  HandCoins,
  ListTodo,
  Receipt,
  Clock,
  MessageSquare
} from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { DashboardFilter, DashboardFilterOption, DashboardSelector } from '@/components/dashboard';
import { useHomepageMetrics } from '@/hooks/useHomepageMetrics';
import { useFilteredMetrics } from '@/hooks/useFilteredMetrics';
import { DataConsistencyChecker } from '@/components/dev/DataConsistencyChecker';
import { useAuth } from '../../hooks/use-auth';
import { useFilter } from '@/contexts/filter-context';
import { getDefaultFilter } from '@/utils/role-based-content';

// Aging Portfolio data is now calculated from master data based on filter

const mockCollectionStrategy = {
  totalInCollection: '£926K',
  strategies: [
    { id: 'strategy-1', name: 'Standard 30-Day Follow-up', status: 'active' as const, amount: '£289K' },
    { id: 'strategy-2', name: 'High-Risk Intensive', status: 'active' as const, amount: '£185K' },
    { id: 'strategy-3', name: 'Post-Dispute Resolution', status: 'paused' as const, amount: '£0' },
    { id: 'strategy-4', name: 'Final Notice Escalation', status: 'active' as const, amount: '£452K' }
  ]
};

export function UnifiedCreditControlDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setCurrentFilter } = useFilter();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>(() => {
    const defaultFilterId = getDefaultFilter(user.role, user.id);
    console.log('[Dashboard] User:', user.name, 'Role:', user.role, 'Default filter ID:', defaultFilterId);
    
    // Find the matching filter option
    const filterOptions: DashboardFilterOption[] = [
      { id: 'everyone', name: 'Manager view', type: 'all' },
      { id: user.id, name: user.name, type: 'user' },
    ];
    
    const defaultOption = filterOptions.find(opt => opt.id === defaultFilterId) || filterOptions[0];
    console.log('[Dashboard] Selected filter option:', defaultOption);
    return defaultOption;
  });
  const [activeStrategyTab, setActiveStrategyTab] = useState<'all' | 'active' | 'paused'>('all');

  // Sync local filter state with global filter context
  // This allows DashboardSelector to react to filter changes
  useEffect(() => {
    const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
    setCurrentFilter(filterId);
    console.log('[Dashboard] Filter changed to:', filterId);
  }, [selectedFilter, setCurrentFilter]);

  // Get metrics from centralized data store (for wins and key metrics)
  const { todaysWins, keyMetrics } = useHomepageMetrics();
  
  // Get filtered metrics based on selected filter
  const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
  const filteredMetrics = useFilteredMetrics(filterId);

  // Static data
  const riskAlerts = [
    {
      id: '1',
      type: 'strategy-inactive' as const,
      severity: 'warning' as const,
      primaryIssue: 'Collection Strategy Inactive',
      context: 'Post-Dispute Resolution paused • 45 customers affected • 0 emails sent in 14 days',
      clickUrl: '/settings/strategies'
    },
    {
      id: '2',
      type: 'customer-risk' as const,
      severity: 'warning' as const,
      customerName: 'Tech Solutions Inc',
      primaryIssue: 'Invoice aging 90+ days - £89,000',
      context: 'Moved from 60-90 days this week',
      clickUrl: '/customers/2'
    },
    {
      id: '3',
      type: 'customer-risk' as const,
      severity: 'urgent' as const,
      customerName: 'Acme Corporation',
      primaryIssue: 'Broken promise - £25,000',
      context: 'Payment was due 3 days ago',
      clickUrl: '/customers/1'
    },
    {
      id: '4',
      type: 'customer-risk' as const,
      severity: 'warning' as const,
      customerName: 'Global Industries Ltd',
      primaryIssue: 'Credit score decreased to 67',
      context: 'Down from 82 last month',
      clickUrl: '/customers/3'
    }
  ];

  // Filter suffix for section headers
  const filterSuffix = selectedFilter.id !== 'everyone' ? ` - ${selectedFilter.name}` : '';

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  // Filter strategies based on active tab
  const filteredStrategies = mockCollectionStrategy.strategies.filter(strategy => {
    if (activeStrategyTab === 'all') return true;
    return strategy.status === activeStrategyTab;
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Map the filtered metrics to the format expected by the dashboard
  const masterPriorities = {
    collectionQueue: {
      count: filteredMetrics.todaysPriorities.casesDueToday.count,
      amount: filteredMetrics.todaysPriorities.casesDueToday.value,
    },
    unallocatedPayments: {
      count: filteredMetrics.todaysPriorities.unallocatedReceipts.count,
      amount: filteredMetrics.todaysPriorities.unallocatedReceipts.value,
    },
    promisesDueToday: {
      dueCount: filteredMetrics.todaysPriorities.promisesDueToday.count,
      brokenCount: filteredMetrics.todaysPriorities.promisesDueToday.brokenCount || 0,
      dueAmount: filteredMetrics.todaysPriorities.promisesDueToday.value,
    },
    disputes: {
      count: filteredMetrics.todaysPriorities.disputesRequiringResponse.count,
      amount: filteredMetrics.todaysPriorities.disputesRequiringResponse.value,
    },
  };
  
  // Calculate aging portfolio bands from filtered metrics
  const aging = filteredMetrics.agingPortfolio;
  const masterAgingPortfolio = {
    totalOutstanding: aging.total,
    bands: [
      { 
        label: 'Current (0-30 days)', 
        amount: aging.current030, 
        percentage: aging.total > 0 ? Math.round((aging.current030 / aging.total) * 100) : 0
      },
      { 
        label: '31-60 Days', 
        amount: aging.over3160, 
        percentage: aging.total > 0 ? Math.round((aging.over3160 / aging.total) * 100) : 0
      },
      { 
        label: '61-90 Days', 
        amount: aging.over6190, 
        percentage: aging.total > 0 ? Math.round((aging.over6190 / aging.total) * 100) : 0
      },
      { 
        label: '91-120 Days', 
        amount: aging.over91120, 
        percentage: aging.total > 0 ? Math.round((aging.over91120 / aging.total) * 100) : 0
      },
      { 
        label: 'Over 120 Days', 
        amount: aging.over121180, 
        percentage: aging.total > 0 ? Math.round((aging.over121180 / aging.total) * 100) : 0
      }
    ]
  };

  // Calculate portfolio overview data for Performance Snapshot
  const over90Amount = aging.over91120 + aging.over121180;
  const over90Percentage = aging.total > 0 ? Math.round((over90Amount / aging.total) * 100) : 0;
  const portfolioOverview = {
    totalOutstanding: aging.total,
    customerCount: filteredMetrics.keyMetrics.customerCount,
    over90Amount,
    over90Percentage
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header Controls - Single Row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Dashboard Toggle Buttons */}
            <DashboardSelector />
            
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {/* Filter Dropdown */}
          <DashboardFilter
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
          />
        </div>

        {/* Global Filter Banner */}
        {selectedFilter.id !== 'everyone' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {selectedFilter.initials || selectedFilter.name.substring(0, 2).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-900">
                Viewing: {selectedFilter.name}'s Portfolio
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFilter({ id: 'everyone', name: 'Manager view', type: 'all' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Dashboard Title */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Credit Control Dashboard</h1>
          <p className="text-sm text-gray-600">
            Real-time overview of outstanding invoices, collection performance, and team activity
          </p>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Section 1: Today's Priorities - 4 Gateway Scorecards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal-600" />
            Today's Priorities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Collection Queue */}
            <NavigationMetricCardAlt
              title="Collection Queue"
              value={masterPriorities.collectionQueue.count.toString()}
              description="Accounts requiring action"
              secondaryValue={formatCurrency(masterPriorities.collectionQueue.amount)}
              icon={Target}
              variant="info"
              onClick={() => navigate('/collections/queue?filter=action-due-today')}
            />

            {/* Unallocated Payments */}
            <NavigationMetricCardAlt
              title="Unallocated Payments"
              value={masterPriorities.unallocatedPayments.count.toString()}
              description="Receipts pending"
              secondaryValue={formatCurrency(masterPriorities.unallocatedPayments.amount)}
              icon={CreditCard}
              variant="info"
              onClick={() => navigate('/payments/receipts?filter=unallocated')}
            />

            {/* Promises Due Today */}
            <NavigationMetricCardAlt
              title="Promises Due Today"
              value={masterPriorities.promisesDueToday.dueCount.toString()}
              description="Due today"
              secondaryValue={`${formatCurrency(masterPriorities.promisesDueToday.dueAmount)}${masterPriorities.promisesDueToday.brokenCount > 0 ? ` | ${masterPriorities.promisesDueToday.brokenCount} broken` : ''}`}
              icon={HandCoins}
              variant="info"
              onClick={() => navigate('/collections/promises?filter=due-today')}
            />

            {/* Disputes */}
            <NavigationMetricCardAlt
              title="Disputes"
              value={masterPriorities.disputes.count.toString()}
              description="Requiring response"
              secondaryValue={formatCurrency(masterPriorities.disputes.amount)}
              icon={Scale}
              variant="info"
              onClick={() => navigate('/disputes?filter=active')}
            />
          </div>

          {/* Today's Progress Summary */}
          <div className="mt-4 pt-4 border-t border-border text-center text-sm text-muted-foreground">
            <CheckCircle2 className="inline-block h-4 w-4 text-green-600 mr-2 -mt-0.5" />
            Today's progress: 
            <span className="font-semibold text-foreground"> 21 payments allocated (£171,100)</span> • 
            <span className="font-semibold text-foreground">7 promises kept</span> • 
            <span className="font-semibold text-foreground">8 cases resolved</span>
          </div>
        </div>

        {/* Section 2: Ageing Analysis + Performance Snapshot (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Ageing Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Ageing Analysis{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {masterAgingPortfolio.bands.map((band, index) => (
                <Link
                  key={index}
                  to={`/analytics-reporting/ageing-analysis?filter=${band.label}`}
                  className="block group"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{band.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">£{(band.amount / 1000000).toFixed(2)}M</span>
                        <span className="text-sm text-gray-500">({band.percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full transition-all group-hover:bg-teal-600"
                        style={{ width: `${band.percentage}%` }}
                      />
                    </div>
                  </div>
                </Link>
              ))}
              
              <div className="pt-4 mt-5 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Total outstanding</span>
                  <span className="text-2xl font-bold text-foreground">
                    £{(masterAgingPortfolio.totalOutstanding / 1000000).toFixed(2)}M
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/analytics-reporting/ageing-analysis')}
                  className="w-full justify-between text-neutral-700 hover:text-teal-600 hover:bg-teal-50"
                >
                  <span className="text-sm font-medium">See your aging debt</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Performance Snapshot - Redesigned */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Snapshot{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Section 1: Key Metrics */}
              <div className="pb-3 border-b border-border">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Key Metrics
                </p>
                <div className="grid grid-cols-4 gap-5">
                  <div>
                    <p className="text-base text-foreground mb-2">Customers</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{portfolioOverview.customerCount}</p>
                    <p className="text-sm text-muted-foreground">
                      Total customers
                    </p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">DSO</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.dso.current}</p>
                    <p className="text-sm text-muted-foreground">
                      Target: {keyMetrics.dso.target} days
                    </p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">Promise Success</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.promiseSuccessRate}%</p>
                    <p className="text-sm text-muted-foreground">21 of 40 kept</p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">Avg Payment Days</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.avgPaymentDays}</p>
                    <p className="text-sm text-muted-foreground">3 days faster</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Collection Rate - Donut Chart */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Collection Rate
                  </p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-2">
                          <p className="font-semibold">Collection Rate Calculation:</p>
                          <p className="text-sm">Cash collected this month ÷ Total cash due this month</p>
                          <p className="text-sm">£1.8M ÷ £4.2M = 43%</p>
                          <p className="text-sm font-semibold mt-2">Target: 85% (industry benchmark)</p>
                          <p className="text-sm">Current gap: 42 percentage points below target</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Donut Chart */}
                <div className="relative w-[200px] h-[200px] my-3 flex items-center justify-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={[
                        { value: todaysWins.collectionRate },
                        { value: 100 - todaysWins.collectionRate }
                      ]}
                      cx={100}
                      cy={100}
                      innerRadius={72}
                      outerRadius={90}
                      startAngle={90}
                      endAngle={450}
                      dataKey="value"
                      stroke="none"
                    >
                      <Cell fill="oklch(0.5 0.09 194.77)" />
                      <Cell fill="oklch(0.9499 0.0059 239.82)" />
                    </Pie>
                  </PieChart>
                  
                  {/* Center Text */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-4xl font-bold text-foreground leading-none">
                      {todaysWins.collectionRate}%
                    </p>
                  </div>
                </div>
                
                <p className="text-[13px] text-muted-foreground text-center flex items-center gap-1">
                  Target: {todaysWins.target}% • <span className="text-warning-600 font-medium">{todaysWins.target - todaysWins.collectionRate} points below target</span>
                  <AlertTriangle className="h-3 w-3 text-warning-500" />
                </p>
              </div>

            </CardContent>
          </Card>
        </div>

        {/* Section 3: Risk Alerts + Collection Strategy (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Risk Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Risk Alerts{filterSuffix}
              </CardTitle>
              <CardDescription>
                {selectedFilter.id !== 'everyone' 
                  ? `Customer-specific risks in ${selectedFilter.name}'s portfolio`
                  : 'High-priority items requiring attention'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Customer-specific Risk Alerts */}
              {riskAlerts.map((alert) => (
                <Link
                  key={alert.id}
                  to={alert.clickUrl}
                  className={`block p-3 rounded-lg border-l-4 transition-all hover:shadow-md ${
                    alert.severity === 'urgent'
                      ? 'bg-red-50 border-red-500 dark:bg-red-950/20 dark:border-red-800'
                      : 'bg-orange-50 border-orange-500 dark:bg-orange-950/20 dark:border-orange-800'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      {alert.type === 'strategy-inactive' ? (
                        <>
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <p className="font-semibold text-sm">{alert.primaryIssue}</p>
                          </div>
                          <p className="text-xs mt-1">{alert.context}</p>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-sm">{alert.customerName}</p>
                          <p className="text-xs mt-1">{alert.primaryIssue}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{alert.context}</p>
                        </>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Collection Strategy */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Collection Strategy{filterSuffix}</CardTitle>
                  <CardDescription>
                    {selectedFilter.id !== 'everyone'
                      ? `Strategies affecting ${selectedFilter.name}'s accounts`
                      : 'Active strategies and performance'}
                  </CardDescription>
                </div>
                <Link to="/settings/strategies" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                  See More
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Half-Donut Gauge */}
              <div className="flex flex-col items-center justify-center py-6">
                <div className="relative w-[240px] h-[160px]">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Collected', value: 77, color: '#10b981' },
                          { name: 'Remaining', value: 23, color: '#E5E7EB' }
                        ]}
                        cx={120}
                        cy={120}
                        innerRadius={80}
                        outerRadius={100}
                        startAngle={180}
                        endAngle={0}
                        paddingAngle={0}
                        dataKey="value"
                        stroke="none"
                      >
                        {[
                          { name: 'Collected', value: 77, color: '#10b981' },
                          { name: 'Remaining', value: 23, color: '#E5E7EB' }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Center text overlay */}
                  <div className="absolute top-[70%] left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                    <div className="text-[32px] font-bold text-gray-900 dark:text-gray-100 leading-none">
                      {mockCollectionStrategy.totalInCollection}
                    </div>
                    <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mt-1">
                      of £1.2M goal
                    </div>
                    <div className="text-[14px] font-semibold text-[#10b981] mt-1">
                      77%
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-4 border-b">
                <button
                  onClick={() => setActiveStrategyTab('all')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'all'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveStrategyTab('active')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'active'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveStrategyTab('paused')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeStrategyTab === 'paused'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Paused
                </button>
              </div>

              {/* Strategy List */}
              <div className="space-y-2">
                {filteredStrategies.map((strategy) => {
                  // Debug logging
                  console.log('[Dashboard] Strategy:', strategy.name, 'ID:', strategy.id);
                  
                  // Ensure strategy has valid ID
                  if (!strategy.id) {
                    console.error('[Dashboard] ERROR: Strategy missing ID:', strategy);
                    return null;
                  }
                  
                  return (
                    <Link
                      key={strategy.id}
                      to={`/settings/strategies/${strategy.id}?tab=performance`}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded ${
                            strategy.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {strategy.status.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{strategy.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{strategy.amount}</span>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Data Consistency Checker (Development Only) */}
      <DataConsistencyChecker />
    </div>
  );
}
